/**
 * Shared analytics event names used across client and server.
 * Keeps PostHog instrumentation consistent and discoverable.
 */
export const ANALYTICS_EVENTS = {
  DOC_APPROVE_CLICKED: "doc_approve_clicked",
  DOC_APPROVE_WITHOUT_EDITS_CONFIRMED: "doc_approve_without_edits_confirmed",
  DOC_APPROVED: "doc_approved",
  DOC_NEXT_ENQUEUED: "doc_next_enqueued",
  TEAM_LINKEDIN_PROFILES_CLICKED: "team_linkedin_profiles_clicked",
  TEAM_LINKEDIN_PROFILES_SUBMITTED: "team_linkedin_profiles_submitted",
  DELIVERABLE_AUTOGEN_ENQUEUED: "deliverable_autogen_enqueued",
  DOMAIN_CHECK: "domain_check",
  LOVABLE_CLICKED: "lovable_clicked",
  BLOCK_CREATED: "block_created",
  DOC_DOWNLOAD_CLICKED: "doc_download_clicked",
  DOC_EDIT_CLICKED: "doc_edit_clicked",
  DOC_SAVE_SUCCEEDED: "doc_save_succeeded",
  DOC_EDIT_DISCARD_DECIDED: "doc_edit_discard_decided",
  DOC_EXPAND_CLICKED: "doc_expand_clicked",
  PROJECT_CREATED: "project_created",
  ANONYMOUS_PROJECT_CREATED: "anonymous_project_created",
  ANONYMOUS_PROJECT_CLAIMED: "anonymous_project_claimed",
  // #prod-new-users Slack pair (LEV-1394): a PostHog destination renders these
  // in #prod-new-users (Hog code -> Slack); nothing in this repo posts to
  // Slack directly. See .claude/skills/posthog-dashboards/SKILL.md.
  ANONYMOUS_PROJECT_CREATION_SUMMARIZED: "anonymous_project_creation_summarized",
  ANONYMOUS_PROJECT_CLAIM_SUMMARIZED: "anonymous_project_claim_summarized",
  // LEV-1594: returning anon visitor whose claim cookie maps to a live project
  // sees a resume notice instead of the onboarding chat input. No PII —
  // landingPageSlug only; projectId omitted because sibling anon events do
  // not include it at impression time.
  ANONYMOUS_PROJECT_RESUME_NOTICE_SHOWN: "anonymous_project_resume_notice_shown",
  ANONYMOUS_PROJECT_RESUME_CANVAS_CLICKED: "anonymous_project_resume_canvas_clicked",
  // LEV-1513: anonymous visitor's canvas finished loading (once per mount;
  // `arrivalCeremony` marks the first-arrival-per-browser auto-open + reveal).
  // Fills the funnel gap between anonymous_project_created and the gate events —
  // $pageview alone can't say the canvas actually rendered with content.
  ANONYMOUS_CANVAS_VIEWED: "anonymous_canvas_viewed",
  // LEV-1396: signup gate impression (a gated action was attempted and the
  // prompt was shown) and signup prompt click (the visitor clicked through to
  // Clerk signup). No PII — action key, projectId, enforcement
  // (configured gate vs hard account boundary), surface (which UI entry
  // point), and on dismissal the method (button/escape/backdrop).
  SIGNUP_GATE_IMPRESSION: "signup_gate_impression",
  SIGNUP_GATE_SIGNUP_CLICKED: "signup_gate_signup_clicked",
  SIGNUP_GATE_DISMISSED: "signup_gate_dismissed",
  LEVAI_CHAT_SESSION_INITIATED: "levai_chat_session_initiated",
  LEVAI_CHAT_DOCUMENT_UPDATED: "levai_chat_document_updated",
  SPOTLIGHT_INTEL_GENERATED: "spotlight_intel_generated",
  FEEDBACK_BUTTON_CLICKED: "feedback_button_clicked",
  /** Reload click on an App Router error boundary (context: route | root; path: the crashed page's pathname) — measures how often founders hit the crash screen and where (PR #755 follow-up). Undercounts root-layout crashes on initial render: PostHog init lives in the crashed tree, and pre-init captures are discarded. */
  ERROR_BOUNDARY_RELOAD_CLICKED: "error_boundary_reload_clicked",
  // LEV-1046: agentic prospecting task. Counts/ids only — no raw task or
  // comment text (per CLAUDE.md analytics rules).
  PROSPECTING_TASK_CREATED: "prospecting_task_created",
  // LEV-2006: a founder (or a job acting on their behalf) hit the Apollo
  // prospect cap. Counts/ids/enum only — never a prospect name or company.
  // `path` names the enforcement point and `checkedBy` says whether the
  // server or the board's own quota readout caught it.
  PROSPECT_CAP_REACHED: "prospect_cap_reached",
  // LEV-2006: a founder at the cap asked for more via the request popup.
  // Counts/booleans only — never the reason text or any email address.
  PROSPECT_MORE_REQUESTED: "prospect_more_requested",
  TASK_COMMENT_POSTED: "task_comment_posted",
  // LEV-1046 follow-up: a founder @-mentioned an agent (Lev/Yuri) on a comment,
  // starting a conversation. Counts/booleans only — never the comment text.
  AGENT_MENTIONED: "agent_mentioned",
  // LEV-1046: task-detail lightbox assignee edit. (TASK_STATUS_CHANGED already
  // exists below and is reused for status edits.)
  TASK_ASSIGNEE_CHANGED: "task_assignee_changed",
  // Lev MCP server (LEV-2323). Counts/IDs only — no document content ever
  // leaves in analytics. The event carrying `projectCount`; `mcp_tool_called`
  // below also fires once for every list_projects call (ok/fail only, no
  // count), so a dashboard counting tool calls should group on
  // mcp_tool_called alone or it will double-count list_projects.
  MCP_PROJECTS_LISTED: "mcp_projects_listed",
  MCP_RESOURCES_LISTED: "mcp_resources_listed",
  MCP_RESOURCE_READ: "mcp_resource_read",
  // LEV-2348: one MCP tool call. Tool name, projectId, an ok boolean, and
  // (LEV-2496) the self-reported agentClient ("claude" | "openai" | null)
  // only: never a title, document text or any other content.
  MCP_TOOL_CALLED: "mcp_tool_called",
  // A founder clicked a "Related Blocks" link in the task lightbox to open the
  // related doc. Ids only (projectId, taskId, blockId) — never the doc title.
  TASK_RELATED_BLOCK_CLICKED: "task_related_block_clicked",
  // Auth events
  USER_SIGNED_UP: "user_signed_up",
  USER_SIGNED_IN: "user_signed_in",
  USER_SIGNED_OUT: "user_signed_out",
  USER_SESSION_EXPIRED: "user_session_expired",
  // Sent only when the Clerk account is under the new-account window, once per
  // browser tab (sessionStorage guard); once per page load when storage is
  // blocked, and PostHog's 30-min per-person mask folds those into one Slack
  // message. It must never fire for an existing user's sign-in: the PostHog
  // Slack destination for new signups is meant to trigger on it (a manual
  // PostHog retarget, see the posthog-dashboards skill).
  USER_FIRST_SESSION_STARTED: "user_first_session_started",
  USER_SIGNUP_VIEWED: "user_signup_viewed",
  USER_SIGNIN_VIEWED: "user_signin_viewed",
  // Onboarding chat events
  ONBOARDING_CHAT_STARTED: "onboarding_chat_started",
  ONBOARDING_CHAT_MESSAGE_SENT: "onboarding_chat_message_sent",
  ONBOARDING_CHAT_SKIP_CLICKED: "onboarding_chat_skip_clicked",
  /** "Start over" confirmed in the landing chat; `detachOk` false = the session could not be detached and the conversation was kept. */
  ONBOARDING_CHAT_RESET: "onboarding_chat_reset",
  ONBOARDING_CHAT_BUILD_CONFIRMED: "onboarding_chat_build_confirmed",
  ONBOARDING_CHAT_BUILD_DECLINED: "onboarding_chat_build_declined",
  ONBOARDING_CHAT_RESTORED: "onboarding_chat_restored",
  ONBOARDING_CHAT_ERROR: "onboarding_chat_error",
  /** "Try again" clicked on a failed-turn notice (LEV-2450). */
  ONBOARDING_CHAT_RETRY_CLICKED: "onboarding_chat_retry_clicked",
  ONBOARDING_CLAIM_TOKEN_DECLINED: "onboarding_claim_token_declined",
  /** Signed-up visitor landed on /onboarding/complete with NO idea signal anywhere (organic sign-in/sign-up, not a handoff) — expected funnel event, never a Sentry error. */
  ONBOARDING_COMPLETE_NO_IDEA_SOURCE: "onboarding_complete_no_idea_source",
  /** An idea signal existed (handoff stash, token, backup, or server session) but conversion could not use any source — the real lost-idea failure, mirrored to Sentry. */
  ONBOARDING_COMPLETE_IDEA_LOST: "onboarding_complete_idea_lost",
  /** Returned to /onboarding/complete for a session already converted to a project (stale redirect, bookmark, or an anonymous project claimed at signup) — routed to the existing project, no second project created. */
  ONBOARDING_COMPLETE_ALREADY_CONVERTED: "onboarding_complete_already_converted",
  /** A signed-in founder with an unsent chat message was sent back to resume it instead of being claimed into "We lost your idea" (LEV-1925). */
  ONBOARDING_COMPLETE_CHAT_RESUMED: "onboarding_complete_chat_resumed",
  ONBOARDING_RESULT_CAPTURED: "onboarding_result_captured",
  ONBOARDING_CONTEXT_BRIDGED: "onboarding_context_bridged",
  ONBOARDING_FOUNDER_CAPTURED: "onboarding_founder_captured",
  ONBOARDING_FIRST_ARTIFACT_COMPLETED: "onboarding_first_artifact_completed",
  // Materials parse events (LEV-1243 Phase 3b, LEV-1295)
  /**
   * Server-side, emitted by the `onboarding-parse-materials` Inngest job once
   * a critique is persisted. Counts and buckets only — never the deck text,
   * page text, file name, or URL. Properties: `sourceType` ("deck" | "site"),
   * `fidelity`, `pageCount` (deck slides, or crawled pages for a site),
   * `slideExtractCount`, `gapCount`, `evidenceSignalCount`, `durationBucket`.
   * Deck only: `deckFormat`. Site only (LEV-1428): `readerName` (the injected
   * SiteReader's provenance, e.g. "cheerio") and `limitation` (boolean — the
   * crawl was partial / an SPA shell; the code itself stays in the critique).
   * Deck only: `settledAfterHandoff` (boolean — the session was frozen to a
   * project when the notify step ran, so the parse sent the late-settle
   * promotion event; it can be true for an on-time deck when the freeze lands
   * between persist and notify, which is safe because promotion is
   * create-only; sites have no downstream promotion step to track, so they
   * never set it).
   */
  ONBOARDING_MATERIALS_PARSED: "onboarding_materials_parsed",
  /**
   * Server-side, `onboarding-parse-materials` job, decks only (ADR 057): ONE
   * row per deck parse's final outcome, for parse-speed analysis
   * (`/deck-parse-speed-report`). `outcome` "ready" fires from the analytics
   * step; "failed" fires from onFailure, so retried attempts never add rows.
   * Properties: timings `queueMs`, `downloadMs`, `prepareMs` (download end →
   * parse start: the previous deck's fact reset), `renderMs`, `visionMs`,
   * `synthesisMs`, `tailMs`, `totalMs` (ms, null when the phase never
   * reported; a failed row carries only `totalMs`, measured event send →
   * failure); `pageCount`, `fileSizeBytes`, `deckFormat` ("pdf" | "pptx");
   * `visionBatches`, `textBatches`, `visionTimeouts` (attempts the per-call
   * timeout aborted), `visionFallbacks` (batches degraded to text),
   * `factsExtracted` (null on failure); `outcome`, `parseProfile`
   * (the APPLIED profile, "standard" | "fast": fast only when the session is
   * proto-blocks ON and the event carried a `deck-parse-fast-disabled`
   * verdict of OFF),
   * `protoBlocksEnabled`, `attempt` (zero-indexed
   * Inngest attempt), `superseded` (true when this run's result was discarded
   * because it no longer held the deck slot at persist time, so its provenance
   * or critique write was refused; always false on failure — exclude these
   * rows from founder-wait latency), `supersededBy` (why, null unless
   * superseded: "reupload" — a newer upload claimed the slot; "settled" — the
   * slot already held another run's result; "sealed" — the session was
   * absent, expired, or frozen without this run's claim). A replayed persist
   * step that finds this run's own critique already settled is not
   * superseded (false / null): that critique is the one the founder sees. Distinct id follows
   * `onboarding_materials_parsed`.
   * Never the file name, deck text, or model output.
   */
  ONBOARDING_DECK_PARSE_TIMED: "onboarding_deck_parse_timed",
  /**
   * Server-side, at `deck_promotion` initialization-phase completion
   * (LEV-1420): the founder's onboarding deck landed in Fundraising without
   * them lifting a finger — original file included, always. Properties:
   * slideCount, `trigger` ("initialization" — the normal claim-time
   * `deck_promotion` phase | "late_settle" — a signed-in founder's parse was
   * still running at handoff and promoted afterward via
   * `onboarding-promote-deck-after-handoff`, so this event can fire once for
   * the same project on a path other than claim).
   */
  FUNDRAISING_DECK_PROMOTED: "fundraising_deck_promoted",
  /**
   * Server-side, emitted by `POST /api/onboarding/materials/parse` when a
   * deck or website parse is accepted (202) or short-circuited because the
   * slot is already on file (200) — LEV-1429. Measures the idempotency hit
   * rate and the deck/site mix. Properties: `sourceType` ("deck" | "site"),
   * `dedupedHit` (boolean — true when nothing was enqueued because the site
   * slot already held a critique or a processing marker). Never the URL, host,
   * or file name.
   */
  ONBOARDING_MATERIALS_PARSE_REQUESTED: "onboarding_materials_parse_requested",
  /**
   * Client-side: an onboarding materials parse failed at some phase
   * (LEV-1295 decks, LEV-1431 websites). Properties: `sourceType`
   * ("deck" | "site"), `phase` ("blob-upload" | "post" | "poll" | "job"),
   * `httpStatus` (number | null), and `fileSizeBucket` for decks only. Never
   * the URL, host, or file name.
   */
  ONBOARDING_MATERIALS_PARSE_FAILED: "onboarding_materials_parse_failed",
  /**
   * A deck upload failed, from any of the four surfaces that take one
   * (onboarding, Fundraising, task-evidence, Labs pitch eval) — LEV-2923.
   * Properties: `kind` (`DeckUploadFailureKind`, `src/lib/utils/deckUploadErrors.ts`),
   * `surface` (`DeckUploadSurface`), `format` (`DeckUploadFormat`), `sizeBucket`
   * (`DeckUploadSizeBucket`, null when the size was never known — e.g. a
   * pre-upload format rejection), `phase` (`DeckUploadPhase`), and an optional
   * `projectId` (omitted pre-claim in onboarding). Never the file name or any
   * extracted text.
   */
  DECK_UPLOAD_FAILED: "deck_upload_failed",
  /**
   * The onboarding build CTA was held back because a deck/site parse was
   * still in flight when the model concluded (LEV-1583). `outcome`:
   * "suppressed" (gate engaged), "nudged" (parse landed ready, a visible
   * nudge message was auto-submitted so Lev delivers the read), "released"
   * (parse failed or the nudge could not be sent; CTA shown as-is).
   * `releaseReason` (LEV-1969, "released" only): "read_delivered" when the
   * just-finished turn's own read-due header already covered the read, so a
   * synthetic nudge would only re-ask Lev to reopen a chat it already closed.
   */
  ONBOARDING_BUILD_CTA_GATED: "onboarding_build_cta_gated",
  /**
   * The founder pressed Enter before Lev had any visible reply to answer
   * against, so the "You can answer once Lev starts replying." hint showed.
   * No properties: this is a rate signal for how often the composer's timing
   * confuses founders. Fired at most once per turn (LEV-1969 QA).
   */
  ONBOARDING_ANSWER_BEFORE_REPLY: "onboarding_answer_before_reply",
  // Landing page navigation events
  LANDING_NAV_CLICKED: "landing_nav_clicked",
  LANDING_LOGIN_CLICKED: "landing_login_clicked",
  LANDING_SIGNUP_CLICKED: "landing_signup_clicked",
  LANDING_CTA_CLICKED: "landing_cta_clicked",
  LANDING_FAQ_TOGGLED: "landing_faq_toggled",
  LANDING_FOOTER_LINK_CLICKED: "landing_footer_link_clicked",
  // LEV-2359 program funnel (flag `accelerators-straight-in`). `program` is on
  // every event; property shapes live on the helpers in client.ts/posthog.ts.
  // The landing_techstars_* slug events keep firing alongside these.
  PROGRAM_LANDING_VIEWED: "program_landing_viewed",
  PROGRAM_CTA_CLICKED: "program_cta_clicked",
  PROGRAM_MATERIALS_SUBMITTED: "program_materials_submitted",
  PROGRAM_FIRST_READ_SHOWN: "program_first_read_shown",
  PROGRAM_BUILD_CLICKED: "program_build_clicked",
  PROGRAM_PROJECT_CLAIMED: "program_project_claimed",
  PROGRAM_APPLICATION_STARTED: "program_application_started",
  /**
   * The founder landed on the "drop into the accelerator features" handoff
   * screen (LEV-2359 Step 6/8): program messaging before Big Idea generation
   * routes them into the program's accelerator features. `hadDeck` is a
   * boolean only — never the file name or parsed contents.
   */
  PROGRAM_HANDOFF_VIEWED: "program_handoff_viewed",
  /**
   * The hand-off screen finished and the founder moved on (LEV-2359 Part 2,
   * group B). `bigIdea`: "built" (the generation call made one), "skipped"
   * (no pending idea to build from, so nothing was generated), "failed"
   * (the generation call rejected), or "timed_out" (it never settled inside
   * the screen's wait budget). `destination`: "application" (the Accelerator
   * Apps page, only after "built") or "canvas" (the project itself, for every
   * other outcome). `durationMs` is how long
   * the screen spent waiting.
   */
  PROGRAM_HANDOFF_COMPLETED: "program_handoff_completed",
  // Defined only; no emitter yet (LEV-2239, LEV-2370, LEV-2372).
  PROGRAM_APPLICATION_SUBMITTED: "program_application_submitted",
  PROGRAM_INTERVIEW_REPORTED: "program_interview_reported",
  PROGRAM_DECISION_REPORTED: "program_decision_reported",
  PROGRAM_TOUGH_QUESTIONS_ANSWERED: "program_tough_questions_answered",
  PROGRAM_APPLICATION_DRAFTED: "program_application_drafted",
  /**
   * Accelerators canvas row (flag `accelerators-row`). `programs` lists only
   * the row-program ids ("techstars", "yc") whose Application block this pick
   * created; re-picking a program already on the row fires nothing.
   */
  ACCELERATOR_PROGRAMS_SELECTED: "accelerator_programs_selected",
  /** A program with no drafted application was taken off the row. `program` is its row-program id. */
  ACCELERATOR_PROGRAM_REMOVED: "accelerator_program_removed",
  /**
   * The Accelerators lightbox opened (flag `accelerators-row`). `view` is
   * "programs" or "application"; `formatId` is the application's format, null
   * on the Programs view.
   */
  ACCELERATOR_LIGHTBOX_OPENED: "accelerator_lightbox_opened",
  /** The founder moved to an application step (`stage`) from the step row or the footer (`via`). */
  ACCELERATOR_APPLICATION_STAGE_SELECTED: "accelerator_application_stage_selected",
  /**
   * Lev's program chip was clicked: "Start your {program} draft"
   * (confirmed=false, opens the Programs view) or "Work on your {program}
   * application" (confirmed=true, opens the application).
   */
  ACCELERATOR_PROGRAM_CHIP_CLICKED: "accelerator_program_chip_clicked",
  /** Lev posted the canned canvas introduction after a first application draft. */
  ACCELERATOR_CANVAS_INTRO_POSTED: "accelerator_canvas_intro_posted",
  /**
   * The Programs nudge became visible to the founder (LEV-2442). `surface`
   * "banner": once per mount on its first visible render. "chat": when Lev's
   * canned line is posted (programsNudgePosted set true).
   */
  ACCELERATOR_PROGRAMS_NUDGE_SHOWN: "accelerator_programs_nudge_shown",
  /** The founder acted on the Programs nudge. `view`/`formatId` name where it sent them. */
  ACCELERATOR_PROGRAMS_NUDGE_CLICKED: "accelerator_programs_nudge_clicked",
  /** The founder dismissed the Programs nudge banner without acting on it. */
  ACCELERATOR_PROGRAMS_NUDGE_DISMISSED: "accelerator_programs_nudge_dismissed",
  /** The signed-in home page's Accelerators announcement banner became visible. Fires once per mount. */
  ACCELERATOR_ANNOUNCEMENT_SHOWN: "accelerator_announcement_shown",
  /** The founder clicked "Open Programs" on the Accelerators announcement banner. */
  ACCELERATOR_ANNOUNCEMENT_CLICKED: "accelerator_announcement_clicked",
  /** The founder dismissed the Accelerators announcement banner without acting on it. */
  ACCELERATOR_ANNOUNCEMENT_DISMISSED: "accelerator_announcement_dismissed",
  /**
   * Lev's one-time Programs line was attached to a chat turn (LEV-2666, flag
   * accelerators-levai-announce). Server-side, once per project, only from the
   * turn whose uiState claim wrote; keyed on the project owner. `variant`
   * "full" describes Programs and asks; "question_only" only asks, because the
   * canned canvas line already described it. Attached, not proof Lev said it.
   */
  LEVAI_PROGRAMS_ANNOUNCED: "levai_programs_announced",
  /**
   * A program founder landed on the canvas at the program first stop (the
   * Programs view with their lander program unconfirmed). Once per visit;
   * `program` is the row-program id ("techstars").
   */
  ACCELERATOR_FIRST_STOP_REACHED: "accelerator_first_stop_reached",
  /**
   * Lev auto-picked a program for a premier-lander founder at the first stop
   * (the automatic pick, `confirmPrograms` source "first-stop"). `program` is
   * the row-program id.
   */
  ACCELERATOR_FIRST_STOP_AUTO_PICKED: "accelerator_first_stop_auto_picked",
  /**
   * The Accelerators lightbox's prev/next block nav switched its view.
   * `from`/`to` are lightboxContentKey-style ids ("programs" or
   * "application:<formatId>").
   */
  ACCELERATOR_BLOCK_NAV_USED: "accelerator_block_nav_used",
  /** The founder clicked a program's "Apply on their site" link. */
  ACCELERATOR_PROGRAM_APPLY_LINK_CLICKED: "accelerator_program_apply_link_clicked",
  /** The founder clicked "Refresh suggestions" on the Programs page (flag `accelerators-program-suggester`). */
  ACCELERATOR_PROGRAM_SUGGESTIONS_REFRESHED: "accelerator_program_suggestions_refreshed",
  /**
   * Server-side: `accelerators.suggestPrograms` produced a stored suggestion
   * set. `trigger` is "auto" (first Programs-view mount) or "refresh".
   */
  ACCELERATOR_PROGRAM_SUGGESTIONS_GENERATED: "accelerator_program_suggestions_generated",
  /** Server-side: `accelerators.suggestPrograms` failed before it could store a result. */
  ACCELERATOR_PROGRAM_SUGGESTIONS_FAILED: "accelerator_program_suggestions_failed",
  /** The founder changed one of the All programs filter dropdowns. `filter` names which one; `value` is the option's enum value, never free text. */
  ACCELERATOR_PROGRAMS_FILTER_CHANGED: "accelerator_programs_filter_changed",
  /** The founder clicked "Clear filters" on the All programs list. `clearedCount` is how many of the 5 filters (region, type, format, deadline, search) were off their default when the click happened. */
  ACCELERATOR_PROGRAMS_FILTERS_CLEARED: "accelerator_programs_filters_cleared",
  /** The founder moved the Think Bigger vision slider in Fundraising's Raise Summary or Investor Deck stage (flag `fundraising-think-bigger`). `level` is the new slider level; `surface` is "raise-summary" (full card) or "deck" (compact card). */
  FUNDRAISING_THINK_BIGGER_LEVEL_CHANGED: "fundraising_think_bigger_level_changed",
  /**
   * The founder paged "What the greats said" (LEV-2442). `surface`:
   * "application" (a program application's Expand stage, flag
   * `accelerators-row`), "raise-summary" or "deck" (flag
   * `fundraising-think-bigger`). `audience` is the program's format id or
   * "investors"; `index` is the 0-based position now shown; `direction` is
   * "previous" or "next". Never the quote itself.
   */
  THINK_BIGGER_QUOTE_PAGED: "think_bigger_quote_paged",
  /**
   * The phone hand-off screen showed (flag `phone-handoff`, LEV-2457).
   * `surface`: "lightbox" (a block or feature opened on a phone), "route" (a
   * Labs page opened on a phone) or "auto_summary" (the once-only summary
   * after the first canvas build).
   */
  PHONE_HANDOFF_SHOWN: "phone_handoff_shown",
  /** The founder copied the project link from the phone hand-off screen. */
  PHONE_HANDOFF_LINK_COPIED: "phone_handoff_link_copied",
  /**
   * Server-side: phoneHandoff.sendLink emailed the founder a link back to the
   * project. `trigger` is "auto" (once per project) or "tap" ("Email me a link").
   */
  PHONE_HANDOFF_EMAIL_SENT: "phone_handoff_email_sent",
  /**
   * Server-side: phoneHandoff.sendLink sent nothing on purpose. `reason`:
   * "no_email", "unconfigured", "already_sent", "throttled" or "not_owner".
   */
  PHONE_HANDOFF_EMAIL_SKIPPED: "phone_handoff_email_skipped",
  /** Server-side: phoneHandoff.sendLink tried to email and Courier failed. */
  PHONE_HANDOFF_EMAIL_FAILED: "phone_handoff_email_failed",
  /** The founder chose "Draft without waiting" on an application held for the Big Idea. */
  ACCELERATOR_DRAFT_WITHOUT_WAITING_CLICKED: "accelerator_draft_without_waiting_clicked",
  /**
   * A homepage capability card ("Validating an idea", ...) was clicked
   * (landing-home-v2). Before the chat starts the click sends the card's
   * starter message (that send is the landing `message_sent` event with
   * source "capability_card"); mid-chat it only re-opens the chat. `card` is
   * the card id only; never the chat text.
   */
  LANDING_CAPABILITY_CARD_CLICKED: "landing_capability_card_clicked",
  /**
   * A homepage Training topic chip ("Pricing", ...) was clicked
   * (landing-home-v2). Same send/re-open behavior as the capability cards,
   * with `message_sent` source "training_topic". `topic` is the topic id only.
   */
  LANDING_TRAINING_TOPIC_CLICKED: "landing_training_topic_clicked",
  // SWW video events
  SWW_VIDEO_OPENED: "sww_video_opened",
  SWW_VIDEO_PLAYED: "sww_video_played",
  SWW_VIDEO_COMPLETED: "sww_video_completed",
  // Lev Learn — in-app concept annotation. Slugs and booleans only; never the
  // surrounding document text (CLAUDE.md analytics rules).
  LEARN_CONCEPT_OPENED: "learn_concept_opened",
  LEARN_CONCEPT_KNOWN_CLICKED: "learn_concept_known_clicked",
  LEARN_CONCEPT_DEEPER_CLICKED: "learn_concept_deeper_clicked",
  // Demand signal for concepts we have not written yet. See the note in
  // client.ts about why the screened term itself is carried.
  LEARN_TERM_SEARCHED: "learn_term_searched",
  LEARN_TERM_MISSED: "learn_term_missed",
  // Someone explicitly asked us to write a concept, rather than merely failing
  // to find it. Much stronger signal than a miss: a miss can be a typo or a
  // stale link, this is a person choosing to press a button.
  LEARN_TERM_REQUESTED: "learn_term_requested",
  // A requester left an address so we can tell them if we publish it. The
  // strongest signal the box produces: they are still interested after the
  // acknowledgement told them most suggestions are not written. Carries the
  // screened term and a boolean, never the address.
  LEARN_TERM_NOTIFY_REQUESTED: "learn_term_notify_requested",
  // LevAI grounded a definition in the corpus rather than improvising it.
  // Carries the resolved slug, never the founder's question.
  LEARN_CONCEPT_LOOKED_UP: "learn_concept_looked_up",
  // A guide page CTA into Lev was clicked. Carries the guide slug, the
  // placement (stage id or "closing") and the stage index, never button copy.
  LEARN_GUIDE_CTA_CLICKED: "learn_guide_cta_clicked",
  // System Card release map filter changed. Carries the filter name and the
  // option key chosen, never free text.
  LEARN_RELEASE_MAP_FILTER: "learn_release_map_filter",
  // System Card edition pages (LEV-2913). Slug, section id, column header and
  // channel only, never the copied or edited share text.
  LEARN_SYSTEM_CARD_SORT: "learn_system_card_sort",
  LEARN_SYSTEM_CARD_SHARE: "learn_system_card_share",
  // A reader downloaded the model routing skill from a System Card edition.
  LEARN_SYSTEM_CARD_SKILL_DOWNLOADED: "learn_system_card_skill_downloaded",
  // Founder apps events
  FOUNDER_APP_CLICKED: "founder_app_clicked",
  // A visitor applied the public Venture Graph directory's search or filters.
  // Carries which controls were used, never the typed text.
  VENTURE_GRAPH_FILTER_APPLIED: "venture_graph_filter_applied",
  // A reviewer accepted or declined an auto-stamped Suggested-add card
  // (LEV-2282 spec). Carries action, path, confidence and ruleVersion only —
  // never the firm name, card id, or any filing text, which is exactly the
  // raw content this screen exists to triage.
  VENTURE_GRAPH_SUGGESTION_DECIDED: "venture_graph_suggestion_decided",
  // YC landing page events
  YC_LANDING_PAGE_VIEWED: "yc_landing_page_viewed",
  YC_LANDING_BUILD_CLICKED: "yc_landing_build_clicked",
  YC_LANDING_SCORE_CLICKED: "yc_landing_score_clicked",
  YC_LANDING_GENERATE_CLICKED: "yc_landing_generate_clicked",
  YC_APP_VIEWED: "yc_app_viewed",
  // A canvas YC first draft (the Application view, the canvas's held drafts,
  // the phone hand-off) is captured server-side by generate once the draft
  // starts and its job is queued (reportYcAppStarted), never for a refused,
  // failed or Regenerate start. With accelerators-row OFF only a named entry
  // point counts there, since the YC canvas banner and block fire on their
  // click. The banner, block and public funnel fire client-side.
  YC_APP_STARTED: "yc_app_started",
  YC_APP_EDIT_CLICKED: "yc_app_edit_clicked",
  YC_APP_SAVE_CLICKED: "yc_app_save_clicked",
  YC_APP_COPY_CLICKED: "yc_app_copy_clicked",
  YC_APP_SCORE_CALCULATED: "yc_app_score_calculated",
  YC_APP_SUGGESTION_ANSWERED: "yc_app_suggestion_answered",
  YC_APP_ONE_LINER_SELECTED: "yc_app_one_liner_selected",
  YC_APP_DISMISSED: "yc_app_dismissed",
  YC_APP_RESTRUCTURE_COMPLETED: "yc_app_restructure_completed",
  YC_APP_THROTTLE_HIT: "yc_app_throttle_hit",
  YC_APP_TURNSTILE_FAILED: "yc_app_turnstile_failed",
  YC_APP_EMAIL_CAPTURED: "yc_app_email_captured",
  YC_APP_INVITE_EMAIL_SENT: "yc_app_invite_email_sent",
  YC_APP_INVITE_URL_VISITED: "yc_app_invite_url_visited",
  YC_APP_INVITE_URL_EXPIRED: "yc_app_invite_url_expired",
  YC_APP_SIGNUP_CLICKED: "yc_app_signup_clicked",
  YC_APP_CLAIMED: "yc_app_claimed",
  YC_APP_CLAIM_EMAIL_MISMATCH: "yc_app_claim_email_mismatch",
  YC_APP_GENERATE_ALL_STARTED: "yc_app_generate_all_started",
  // Founder Type events
  FOUNDER_TYPE_PAGE_VIEWED: "founder_type_page_viewed",
  FOUNDER_TYPE_TEST_STARTED: "founder_type_test_started",
  FOUNDER_TYPE_QUESTION_ANSWERED: "founder_type_question_answered",
  FOUNDER_TYPE_TEST_COMPLETED: "founder_type_test_completed",
  FOUNDER_TYPE_RESULT_VIEWED: "founder_type_result_viewed",
  FOUNDER_TYPE_CARD_COPIED: "founder_type_card_copied",
  FOUNDER_TYPE_CARD_DOWNLOADED: "founder_type_card_downloaded",
  FOUNDER_TYPE_LINKEDIN_SHARED: "founder_type_linkedin_shared",
  FOUNDER_TYPE_BUILD_CLICKED: "founder_type_build_clicked",
  FOUNDER_TYPE_CARD_FLIPPED: "founder_type_card_flipped",
  // LevAI chat interaction events
  LEVAI_CHAT_MESSAGE_SENT: "levai_chat_message_sent",
  LEVAI_CHAT_MESSAGE_RECEIVED: "levai_chat_message_received",
  LEVAI_CHAT_CHIP_SELECTED: "levai_chat_chip_selected",
  LEVAI_CHAT_EDIT_ACCEPTED: "levai_chat_edit_accepted",
  LEVAI_CHAT_EDIT_REJECTED: "levai_chat_edit_rejected",
  LEVAI_CHAT_HISTORY_CLEARED: "levai_chat_history_cleared",
  LEVAI_CHAT_PANEL_TOGGLED: "levai_chat_panel_toggled",
  LEVAI_CHAT_STREAM_ERROR: "levai_chat_stream_error",
  LEVAI_CHAT_SESSION_SUMMARY: "levai_chat_session_summary",
  LEVAI_CHAT_ABANDONMENT: "levai_chat_abandonment",
  LEVAI_LLM_REQUEST_STARTED: "levai_llm_request_started",
  LEVAI_LLM_REQUEST_COMPLETED: "levai_llm_request_completed",
  LEVAI_LLM_REQUEST_FAILED: "levai_llm_request_failed",
  // LevAI performance metrics
  LEVAI_PERF_RESPONSE_TIME: "levai_perf_response_time",
  LEVAI_PERF_TOOL_EXECUTION_TIME: "levai_perf_tool_execution_time",
  LEVAI_PERF_TIMEOUT: "levai_perf_timeout",
  LEVAI_MODELS: "levai_models",
  // LevAI tool call events
  LEVAI_TOOL_GET_BLOCK_CONTENT: "levai_tool_get_block_content",
  LEVAI_TOOL_PROPOSE_EDIT: "levai_tool_propose_edit",
  LEVAI_TOOL_VIEWPORT_MOVE: "levai_tool_viewport_move",
  LEVAI_TOOL_VIEWPORT_FIT_ALL: "levai_tool_viewport_fit_all",
  LEVAI_TOOL_CREATE_DELIVERABLE: "levai_tool_create_deliverable",
  LEVAI_TOOL_REFRESH_BLOCK_INDEX: "levai_tool_refresh_block_index",
  LEVAI_TOOL_APPROVE_DOCUMENT: "levai_tool_approve_document",
  LEVAI_TOOL_CONTINUE_TO_NEXT_STEP: "levai_tool_continue_to_next_step",
  LEVAI_TOOL_GENERATE_SECTION: "levai_tool_generate_section",
  LEVAI_TOOL_ERROR: "levai_tool_error",
  LEVAI_TOOL_RETURN_PARSE_ERROR: "levai_tool_return_parse_error",
  // LevAI sandbox runtime events
  LEVAI_SANDBOX_LEASE_REQUESTED: "levai_sandbox_lease_requested",
  LEVAI_SANDBOX_INSTANCE_STARTED: "levai_sandbox_instance_started",
  LEVAI_SANDBOX_INSTANCE_COMPLETED: "levai_sandbox_instance_completed",
  LEVAI_SANDBOX_INSTANCE_FAILED: "levai_sandbox_instance_failed",
  LEVAI_SANDBOX_INSTANCE_CANCELLED: "levai_sandbox_instance_cancelled",
  LEVAI_SANDBOX_INSTANCE_EXPIRED: "levai_sandbox_instance_expired",
  LEVAI_SANDBOX_CONVERSATION_CLEANUP_COMPLETED:
    "levai_sandbox_conversation_cleanup_completed",
  LEVAI_SANDBOX_CONVERSATION_CLEANUP_FAILED:
    "levai_sandbox_conversation_cleanup_failed",
  LEVAI_SANDBOX_RUNTIME_QUERY_TOOL_CALLED:
    "levai_sandbox_runtime_query_tool_called",
  LEVAI_SANDBOX_RUNTIME_ACTION_TOOL_CALLED:
    "levai_sandbox_runtime_action_tool_called",
  LEVAI_SANDBOX_RUNTIME_GUIDANCE_REQUESTED:
    "levai_sandbox_runtime_guidance_requested",
  LEVAI_SANDBOX_RUNTIME_DRAFT_SUBMITTED:
    "levai_sandbox_runtime_draft_submitted",
  LEVAI_SANDBOX_RUNTIME_MEMORY_CANDIDATES_SUBMITTED:
    "levai_sandbox_runtime_memory_candidates_submitted",
  LEVAI_SANDBOX_RUNTIME_HEARTBEAT: "levai_sandbox_runtime_heartbeat",
  LEVAI_SANDBOX_RUNTIME_COMPLETE_CALLED:
    "levai_sandbox_runtime_complete_called",
  LEVAI_SANDBOX_RUNTIME_AGENT_EVENT_EMITTED:
    "levai_sandbox_runtime_agent_event_emitted",
  LEVAI_SANDBOX_RUNTIME_AUTH_REJECTED:
    "levai_sandbox_runtime_auth_rejected",
  LEVAI_SANDBOX_PERF_RUNTIME_OPERATION_TIME:
    "levai_sandbox_perf_runtime_operation_time",
  LEVAI_SANDBOX_PERF_PROVIDER_OPERATION_TIME:
    "levai_sandbox_perf_provider_operation_time",
  LEVAI_SANDBOX_PERF_TOTAL_TIME: "levai_sandbox_perf_total_time",
  LEVAI_SANDBOX_SESSION_SUMMARY: "levai_sandbox_session_summary",
  // Task generation orchestration events (whole-run fan-out; per-task lifecycle lives below under TASK_*)
  TASKS_GENERATE_STARTED: "tasks_generate_started",
  TASKS_GENERATE_DISPATCHED: "tasks_generate_dispatched",
  TASKS_GENERATE_FAILED: "tasks_generate_failed",
  // Skeleton design returned zero quests after the retry nudge (stall audit
  // #18). Counts + ids only — the context counts distinguish sanctioned
  // "open tasks cover the need" empties from model flakiness.
  TASKS_SKELETON_EMPTY_RESULT: "tasks_skeleton_empty_result",
  // Pre-generated hidden tasks flipped visible synchronously by
  // addMore/replenish (counts only, no task text).
  TASKS_BUFFER_REVEALED: "tasks_buffer_revealed",
  // AI generation events (all generateGpt5 calls). The onboarding chat's row
  // also carries `preStreamMs` (handler entry to model call: rate limit, body
  // parse, session and flag reads, the synchronous LinkedIn scan, prompt
  // assembly) and `firstOutputMs` (model call to the first streamed chunk,
  // null when the client left before one arrived), LEV-2395.
  AI_GENERATION_COMPLETED: "ai_generation_completed",
  /**
   * Server-side, `ai.generateIdeaStatement`, LEV-2395: ONE row per Big Idea
   * request that finished generating, so the request's wall time can be split
   * by phase. Every property is a duration in ms, absent when the phase did
   * not run: `authority` (claim acquisition), `serverInput` (auto-cascade
   * only), `context`, `ai`, `dbWrite`, `events` (the whole post-write tail),
   * `total`, and one `postWrite*` key per post-write step inside `events`
   * (`postWriteContentChanged`, `postWriteCompassRefresh`,
   * `postWriteAdditionalIdeaSummary`, ...; the step names are
   * `DocumentGenerationPostWritePhase` in camelCase). A resumed run has no
   * `context` or `ai`. Never idea text or model output.
   *
   * Under `idea-deferred-finish` the tail runs after the response: the row
   * also carries `response` (request start to the response), fires only once
   * the tail finishes (no row when it fails), and `total` runs to the tail's
   * end. `postWriteAdditionalIdeaSummary` is then the in-request write, and
   * `events` covers only the deferred tail. `response` is absent when the
   * whole tail ran inside the request.
   */
  IDEA_GENERATION_TIMED: "idea_generation_timed",
  // PostHog's reserved LLM-Analytics event name; the leading "$" is required by
  // PostHog and exempts this value from the snake_case convention above.
  AI_GENERATION: "$ai_generation",
  // LEV-1243: stage compass autonomy decision (per-step sufficiency verdicts)
  GENERATION_AUTONOMY_DECIDED: "generation_autonomy_decided",
  BLOCK_AUTO_GENERATION_DISPATCHED: "block_auto_generation_dispatched",
  BLOCK_AUTO_GENERATION_FINISHED: "block_auto_generation_finished",
  // Client-side: the cascade hit its dispatch-attempt cap and stopped trying.
  // The server emits BLOCK_AUTO_GENERATION_FINISHED per refused attempt, but
  // only the client knows it gave up — without this the founder's cascade
  // stalls with no telemetry on either side.
  BLOCK_AUTO_GENERATION_ABANDONED: "block_auto_generation_abandoned",
  // LEV-1243: stage compass surfaced a self-report-vs-evidence conflict
  STAGE_CONFLICT_SURFACED: "stage_conflict_surfaced",
  // LEV-1243: 1xUX draft-signoff events
  BLOCK_SIGNED_OFF: "block_signed_off",
  // proto-lifecycle: the founder clicked "Improve this" on a PROTO block,
  // dispatching the enhance entry point. Emitted at the click boundary.
  PROTO_BLOCK_IMPROVE_CLICKED: "proto_block_improve_clicked",
  // proto-blocks enhance: a PROTO block began / finished developing into a
  // DRAFT. Emitted at the client boundaries around the enhance generation.
  PROTO_BLOCK_ENHANCE_STARTED: "proto_block_enhance_started",
  PROTO_BLOCK_ENHANCE_COMPLETED: "proto_block_enhance_completed",
  // proto-blocks creation: primaries materialized as PROTO blocks from facts.
  // Server-side at both creation paths. Counts only, no fact values (ADR 056).
  PROTO_BLOCKS_MATERIALIZED: "proto_blocks_materialized",
  /**
   * proto-blocks fact finding (ADR 056), server-side, proto-blocks ON only.
   * One per completed extraction run: a chat turn's extraction job, a deck
   * parse (aggregated over its batches) or a site crawl, including late
   * post-creation parses (projectId set). Coverage only — keys, counts,
   * lengths, never a value. Properties: onboardingSessionId, projectId|null,
   * source (chat|deck|site), extractorVersion, modelId, durationMs, factCount,
   * factKeysPresent, valueLengthByKey, messageIndex (chat) and pageCount (deck),
   * each null otherwise.
   */
  ONBOARDING_FACTS_EXTRACTED: "onboarding_facts_extracted",
  /**
   * proto-blocks creation (ADR 056): the session's fact coverage at project
   * creation, both creation paths, proto-blocks ON only. Properties:
   * onboardingSessionId, projectId, totalFactCount, factKeysPresent,
   * factKeysBySource {chat,deck,site}, valueLengthByKey, protoStepsSufficient,
   * protoStepsInsufficient, sectionCoverageByStep {step:{filled,needsInput,
   * research}}, protoCount. Never a value.
   */
  PROTO_BLOCKS_FACTS_SUMMARY: "proto_blocks_facts_summary",
  // proto-blocks late parse: a capture block's "not yet known" lines were
  // filled from late facts, and the layout-drift warning counterpart.
  CAPTURE_BLOCK_LATE_MERGED: "capture_block_late_merged",
  CAPTURE_BLOCK_LAYOUT_DRIFT: "capture_block_layout_drift",
  ICP_CHOSEN: "icp_chosen",
  // levai-typed-icp. Client-side: the founder clicked "describe my own" under
  // the Customer Discovery candidate chips. Properties: projectId,
  // candidateCount. Never the founder's ICP text.
  ICP_DESCRIBE_OWN_CLICKED: "icp_describe_own_clicked",
  // Customer Discovery rebuild (row-order spec). Server-side, fired once when
  // a project is stamped with the v2 row at creation (`customerDiscoveryV2`
  // Project column) — the funnel denominator for every later v2 event.
  // Properties: projectId only.
  CUSTOMER_DISCOVERY_V2_ASSIGNED: "customer_discovery_v2_assigned",
  // Server-side, fired once when the owner opts a legacy project into the v2
  // row (`icp.migrateLegacyProject`). Separate from ASSIGNED so the
  // creation-time funnel denominator stays new projects only. Properties:
  // projectId only.
  CUSTOMER_DISCOVERY_V2_MIGRATED: "customer_discovery_v2_migrated",
  // ICP tile (icp-tile wiring plan). Client-side: a founder toggled or added a
  // chip on one of the four dials. `dial` is the dial id ("role", "seniority",
  // "size", "hq"), never founder-entered text.
  ICP_DIAL_CHANGED: "icp_dial_changed",
  // ICP builder (icp-builder spec). Server-side, fired from `icp.save` on the
  // mutation that persists the ICP (mirrors the server-side capture of
  // INVESTOR_RESEARCH_ROUND_SAVED / MA_ADVISOR_MANDATE_SAVED: the save is a
  // tRPC mutation, not a pure UI click). Counts/booleans only — never the
  // dial values themselves.
  ICP_SAVED: "icp_saved",
  // ICP tile. Server-side, after commit: the founder resolved a Needs review
  // state by accepting Lev's suggested dials or keeping the saved ICP.
  // Properties: projectId, choice ("accept" | "keep").
  ICP_SUGGESTION_RESOLVED: "icp_suggestion_resolved",
  // Customer Targets (tile 03). Server-side from the find-targets job after a
  // saved ICP filled "Lev found". Properties: projectId, count, replaced
  // (untouched cards removed by a re-save), outcome ("found" |
  // "search_failed" | "no_role"). Never the dial values or people.
  CUSTOMER_TARGETS_FOUND: "customer_targets_found",
  // Customer Targets. Server-side from the enrich job, once per card the
  // find-targets job created (and per retry on a move to Contacted).
  // Properties: projectId, outcome ("enriched" | "no_path" | "failed").
  CUSTOMER_TARGET_ENRICHED: "customer_target_enriched",
  // Customer Targets. Client-side: the founder set a card's reach path.
  // Properties: projectId, reachPath ("cold" | "warm"). Never the "via" text.
  CUSTOMER_TARGET_REACH_PATH_SET: "customer_target_reach_path_set",
  // ICP tile, server-side from the draft job: Lev's draft landed, or the run
  // gave up (after retries, or its dispatch failed). Properties: projectId,
  // mode ("draft" = first draft, "suggest" = suggestion for a saved ICP).
  ICP_DRAFT_COMPLETED: "icp_draft_completed",
  ICP_DRAFT_FAILED: "icp_draft_failed",
  DELIVERABLE_APPROVED: "deliverable_approved",
  AGENT_TASK_CREATED: "agent_task_created",
  AI_GENERATION_FAILED: "ai_generation_failed",
  // Project lifecycle events
  PROJECT_RENAMED: "project_renamed",
  // Captured client-side today (trackProjectDeleted) AND server-side by
  // projects.deleteNow (LEV-2012). The delete UI slice that switches the
  // landing page off projects.softDelete MUST drop the client call, or every
  // delete counts twice. The two also carry different property shapes today
  // (client `projectId`; server `lev_project_id` + `wasArchived`), so a
  // dashboard on this event sees mixed rows until the client call is gone.
  PROJECT_DELETED: "project_deleted",
  // Client-side, fired whenever the delete confirmation dialog opens (LEV-2015).
  // The denominator for `project_archived` filtered to `source: "delete_dialog"` —
  // without it there is no rate for how often the dialog's "Archive instead"
  // steer converts a founder who opened Delete… into an archive instead.
  PROJECT_DELETE_DIALOG_OPENED: "project_delete_dialog_opened",
  // Project archive lifecycle (LEV-2012). Server-side, owner-only; ids only,
  // plus a `source` property: "card_menu" (the card's Archive item) or
  // "delete_dialog" ("Archive instead" inside the delete confirmation).
  PROJECT_ARCHIVED: "project_archived",
  PROJECT_UNARCHIVED: "project_unarchived",
  // Restored from the soft-deleted state before the purge window closed.
  PROJECT_RESTORED: "project_restored",
  // LEV-2016. Emitted by the purge itself (the cron and the on-request job),
  // at the one boundary that knows destruction actually completed — after the
  // row and its files are gone, never when a candidate is merely selected.
  // Properties are ids and counts only: `deletedForDays`, `wasArchived`, and
  // `immediate` (the founder asked to skip the rest of the window). Never
  // emitted for an anonymous project: the sentinel owner is not a person.
  // Paired with PROJECT_RESTORED, this is the only way to answer how often a
  // founder restores versus lets a project go.
  PROJECT_PURGED: "project_purged",
  // LEV-2016, `projects.purgeNow`: the founder asked to destroy a deleted
  // project now rather than wait out the window. Emitted after the marker is
  // committed, so it counts intent; PROJECT_PURGED counts completion.
  PROJECT_PURGE_REQUESTED: "project_purge_requested",
  // Orbit (People DB) — open the selected project from the global view (LEV-889)
  ORBIT_OPEN_PROJECT_CLICKED: "orbit_open_project_clicked",
  // Orbit (People DB) — opened the LinkedIn import guide modal (LEV-925)
  ORBIT_LINKEDIN_IMPORT_GUIDE_OPENED: "orbit_linkedin_import_guide_opened",
  // Orbit (People DB) — opened the Google Contacts import guide modal (LEV-1044)
  ORBIT_GOOGLE_IMPORT_GUIDE_OPENED: "orbit_google_import_guide_opened",
  // Orbit / pipeline list view — changed sort (LEV-1019)
  ORBIT_LIST_SORT_CHANGED: "orbit_list_sort_changed",
  // Orbit / pipeline list view — applied/cleared a filter (LEV-1019)
  ORBIT_LIST_FILTER_CHANGED: "orbit_list_filter_changed",
  // Orbit / pipeline list view — toggled a column's visibility (LEV-1019)
  ORBIT_LIST_COLUMN_TOGGLED: "orbit_list_column_toggled",
  // Orbit / pipeline list view — reordered properties via drag (LEV-1080)
  ORBIT_LIST_COLUMN_REORDERED: "orbit_list_column_reordered",
  // Orbit (People DB) — opened the lightbox from a people-import notification (LEV-1070)
  ORBIT_NOTIFICATION_CLICKED: "orbit_notification_clicked",
  // Orbit Scan → Lev Universe cache — a founder ran a Scan (LEV-1010). PII-free:
  // counts + booleans only (target/hit/miss/enriched/failed counts, credits).
  ORBIT_SCAN_COMPLETED: "orbit_scan_completed",
  // Orbit ICP prospecting — a scan finished and produced its candidate list
  // (LEV-1207). Carries the score distribution so we can tell whether the
  // match threshold is calibrated: a high `peopleScanned` with a near-zero
  // `candidateCount` means founders are staring at an empty drawer.
  // `outcome` (LEV-1574): "ok" carries the stats; a failed scan carries only
  // the outcome, so it never reads as a zero-candidate scan.
  ORBIT_PROSPECT_SCANNED: "orbit_prospect_scanned",
  // Orbit ICP prospecting — the founder confirmed their review (LEV-1207).
  // Comparing the score of accepted vs rejected candidates is how we learn
  // whether the scorer agrees with the founder.
  ORBIT_PROSPECT_CONFIRMED: "orbit_prospect_confirmed",
  // Pipeline — generated a personalized "Draft email" from a first-column card (LEV-1108)
  PIPELINE_DRAFT_EMAIL_GENERATED: "pipeline_draft_email_generated",
  // Pipeline toolbar — a founder dwelt on or focused a toolbar control long
  // enough to see its hint tooltip (LEV-1093 hints A/B). No copy in payload.
  PIPELINE_TOOLBAR_HINT_VIEWED: "pipeline_toolbar_hint_viewed",
  // Guided pipeline tour (the other teaching surface, alongside the toolbar
  // hints). No copy in any of these payloads: `stop` identifies which tour
  // stop, never its rendered text.
  PIPELINE_TOUR_STARTED: "pipeline_tour_started",
  PIPELINE_TOUR_STEP_VIEWED: "pipeline_tour_step_viewed",
  PIPELINE_TOUR_COMPLETED: "pipeline_tour_completed",
  PIPELINE_TOUR_SKIPPED: "pipeline_tour_skipped",
  // Guided Overall Evaluation tour (`evaluation-tour` flag). Same contract as
  // the pipeline tour events: `stop` is a stop id, never its rendered text.
  EVALUATION_TOUR_STARTED: "evaluation_tour_started",
  EVALUATION_TOUR_STEP_VIEWED: "evaluation_tour_step_viewed",
  EVALUATION_TOUR_COMPLETED: "evaluation_tour_completed",
  EVALUATION_TOUR_SKIPPED: "evaluation_tour_skipped",
  // A Lev Learn link in a tour stop's footer; `slug` is the Term's slug, never its text.
  EVALUATION_TOUR_LEARN_CLICKED: "evaluation_tour_learn_clicked",
  // General pipeline-board funnel events, added alongside the tour so its
  // adoption can be read against overall board usage. `action` on
  // PIPELINE_BOARD_ACTION is a closed union of control identifiers, never a
  // row id or free text.
  PIPELINE_BOARD_VIEWED: "pipeline_board_viewed",
  PIPELINE_BOARD_ACTION: "pipeline_board_action",
  // The board told the founder that cards arrived: one event per toast shown.
  // `announcement` is a closed id and `count` the number the toast showed;
  // never a name, email, entry id or the toast's text.
  PIPELINE_ENTRY_ANNOUNCED: "pipeline_entry_announced",
  // Collaboration & sharing events (LEV-553)
  PROJECT_SHARED: "project_shared",
  PROJECT_INVITE_SENT: "project_invite_sent",
  SHARE_EMAIL_DRAFT_GENERATED: "share_email_draft_generated",
  SHARE_EMAIL_DRAFT_OPENED: "share_email_draft_opened",
  SHARE_EMAIL_COPIED: "share_email_copied",
  SHARE_COMMENT_ADDED: "share_comment_added",
  SHARE_COMMENT_REPLY_ADDED: "share_comment_reply_added",
  SHARE_COMMENT_THREAD_VIEWED: "share_comment_thread_viewed",
  SHARE_COMMENT_HIDDEN: "share_comment_hidden",
  SHARE_COMMENT_PIN_MOVED: "share_comment_pin_moved",
  SHARE_PAGE_VIEWED: "share_page_viewed",
  // Report sharing events
  SHARE_WEEKLY_REPORT_SHARED: "share_weekly_report_shared",
  SHARE_REPORTS_COLLECTION_SHARED: "share_reports_collection_shared",
  SHARE_REPORTS_COLLECTION_LINK_COPIED: "share_reports_collection_link_copied",
  SHARE_REPORTS_COLLECTION_DISABLED: "share_reports_collection_disabled",
  // Weekly-report share "ask" set + inline viewer comment added. Counts/booleans/
  // lengths/ids only — never the raw ask or comment text (per CLAUDE.md analytics rules).
  WEEKLY_REPORT_ASK_SET: "weekly_report_ask_set",
  WEEKLY_REPORT_COMMENT_ADDED: "weekly_report_comment_added",
  // Block sharing events
  SHARE_BLOCK_SHARED: "share_block_shared",
  SHARE_BLOCK_DISABLED: "share_block_disabled",
  // Block-level comments (LEV-594)
  BLOCK_COMMENT_CREATED: "block_comment_created",
  BLOCK_COMMENT_REPLIED: "block_comment_replied",
  BLOCK_COMMENT_MENTION_SENT: "block_comment_mention_sent",
  BLOCK_COMMENT_NOTIFICATION_CLICKED: "block_comment_notification_clicked",
  COLLABORATOR_INVITED: "collaborator_invited",
  COLLABORATOR_REMOVED: "collaborator_removed",
  COLLABORATOR_TASK_PERMISSIONS_CHANGED: "collaborator_task_permissions_changed",
  COLLABORATION_INVITE_EMAIL_CLICKED: "collaboration_invite_email_clicked",
  COLLABORATION_INVITE_ACCEPTED: "collaboration_invite_accepted",
  // Task lifecycle events (LEV-553)
  TASK_CREATED: "task_created",
  // An eval path step's first move from pending into in_progress, or straight
  // to completed (`planEvalTaskEvents`). A gated "+" and a defer are not
  // Added. Once per task.
  TASK_ADDED: "task_added",
  TASK_COMPLETED: "task_completed",
  TASK_STATUS_CHANGED: "task_status_changed",
  // Founder approved a Lev completion gate (button or "Approved" comment).
  TASK_COMPLETION_APPROVED: "task_completion_approved",
  // Lev work review (gated by task-proposal-edit): founder sent feedback
  // (edit) or discarded the work (reject) on a proposal card or an approval
  // request. One event covers both surfaces so edit-vs-reject rates are
  // comparable across them. No feedback text (PII rule).
  TASK_LEV_WORK_REVIEWED: "task_lev_work_reviewed",
  TASK_EDITED: "task_edited",
  TASK_DEFERRED: "task_deferred",
  TASK_DELETED: "task_deleted",
  // Date-scheduling kanban (LEV-849, gated by task-schedule-kanban)
  TASK_SCHEDULED_VIA_DRAG: "task_scheduled_via_drag",
  TASK_KANBAN_OPENED: "task_kanban_opened",
  TASK_KANBAN_COLLAPSED: "task_kanban_collapsed",
  // task-kanban "All tasks" board (gated by task-kanban): view switch, week
  // navigation and the My-tasks filter. Ids and enums only, no task text.
  TASK_KANBAN_MODE_CHANGED: "task_kanban_mode_changed",
  TASK_KANBAN_WEEK_NAVIGATED: "task_kanban_week_navigated",
  TASK_KANBAN_MY_TASKS_TOGGLED: "task_kanban_my_tasks_toggled",
  // task-kanban scheduled start (server, distinct id = project owner): the
  // job flipped a dated pending task to in_progress on its day, and a Lev
  // run the job (or a defer to today) started finished its start step or
  // skipped its claim (outcome "skipped" + reason). Ids, enums and minutes
  // only, never a title or comment body.
  TASK_SCHEDULED_START_FLIPPED: "task_scheduled_start_flipped",
  TASK_SCHEDULED_LEV_RUN_FINISHED: "task_scheduled_lev_run_finished",
  // "Add your own task" from a board column: which view and which kind of
  // column, never the task text.
  TASK_KANBAN_TASK_ADDED: "task_kanban_task_added",
  // Overdue-snapshot cron: one event per overdue task per Fri–Thu week,
  // distinct id = project owner. A retry resends the same uuid + timestamp,
  // which PostHog merges only eventually, so count distinct taskId+weekKey,
  // never raw rows. `$insert_id` (task+weekKey) is a query key, not a dedup
  // key. Ids/ints/enums only.
  TASK_OVERDUE_SNAPSHOT: "task_overdue_snapshot",
  // Every durable `actions.defer` (app UI, LevAI tool calls, and the Lev MCP
  // server alike; `trigger: "defer"`), and every start that moves a task's
  // day to today (`trigger: "start"`): starting an overdue task counts as
  // rescheduling it to today. Deliberately NOT deduped with `$insert_id` — unlike
  // `action_deferred`, which is per-task by design for the dashboard, this
  // is a per-reschedule log. Ids/days/enums only, never a task title.
  TASK_RESCHEDULED: "task_rescheduled",
  TASK_OPENED: "task_opened",
  // Per-user read state (Q3). task_marked_read fires at the successful
  // markRead boundary in TaskLightbox — the one point every open path shares
  // (member panel and evaluation opens don't emit task_opened). Unread is the
  // explicit kebab "Mark as unread" action.
  TASK_MARKED_READ: "task_marked_read",
  TASK_MARKED_UNREAD: "task_marked_unread",
  // Sidebar drag-to-reorder within a status section. `taskCount` is the number
  // of rows whose sortOrder changed — no titles or user content.
  TASK_LIST_REORDERED: "task_list_reordered",
  TASK_COMPLETION_NOTES_ADDED: "task_completion_notes_added",
  // Conscious note-taker guided flow (task-notes-v3): one event, low-cardinality
  // `phase` property (begun | note_submitted | followup_asked | proposal_rejected |
  // proposal_regenerated) so rollout + drop-off are measurable per step.
  TASK_COMPLETION_CHECKIN: "task_completion_checkin",
  // Progress updates on open human tasks (task-progress-sync): one event, low-
  // cardinality `phase` (skipped_trivial | classified_chatter |
  // classifier_failed | proposed | no_target | aborted | draft_failed |
  // accepted | rejected) + `source` (comment | completion-notes).
  // Ids and enums only, never the update text.
  TASK_PROGRESS_SYNC: "task_progress_sync",
  TASK_WEEKLY_REPORT_GENERATED: "task_weekly_report_generated",
  TASK_WEEKLY_REPORT_FOUNDER_UPDATED: "task_weekly_report_founder_updated",
  TASK_WEEKLY_REPORT_OPENED: "task_weekly_report_opened",
  TASK_CREATE_CLICKED: "task_create_clicked",
  TASK_WEEKLY_REPORT_CLICKED: "task_weekly_report_clicked",
  // Mobile-web prototype (day-1 build) — same funnels as desktop, distinct
  // events so mobile usage is visible on its own rather than folded into
  // desktop task/chat events with different required properties.
  // MOBILE_LEV_MESSAGE_SENT: `surface` (today_cta | brain_dump | review),
  // `messageLength`, and on review only `via` (button = "Improve it",
  // composer = the founder's own typed or spoken change). Never the text.
  MOBILE_LEV_MESSAGE_SENT: "mobile_lev_message_sent",
  MOBILE_IMPACT_CTA_TAPPED: "mobile_impact_cta_tapped",
  MOBILE_PROJECT_SWITCHED: "mobile_project_switched",
  // Pairs with LEVAI_CHAT_EDIT_ACCEPTED: without the decline side, the
  // accept-versus-discard rate on a proposed canvas edit reads as unanimous.
  MOBILE_PROPOSAL_DISCARDED: "mobile_proposal_discarded",
  // A phone was sent from the canvas to the mobile app by the `mobile-app`
  // flag. The only way to tell an intentional visit from an automatic one, and
  // so the only way to read whether the redirect helps or annoys.
  MOBILE_AUTO_REDIRECTED: "mobile_auto_redirected",
  // A document approved from the phone. `doc_approved` already fires
  // server-side for these (same `documents.approve` path as the canvas), but it
  // carries nothing that separates a phone approval from a desktop one — and
  // approving on a phone is the single thing this surface exists to make
  // possible, so it has to be countable on its own.
  MOBILE_DOC_APPROVED: "mobile_doc_approved",
  // Task roadmap (gated by task-roadmap) — founder actions on the roadmap-driven
  // sidebar. `nodeId` is a fixed template key (e.g. "gtm"), never user content.
  // Roadmap board opener in the task-list header — measures adoption of the
  // narrowed task-roadmap flag. Only `projectId` (no user content).
  ROADMAP_BOARD_OPENED: "roadmap_board_opened",
  ROADMAP_TASK_ADDED_TO_LIST: "roadmap_task_added_to_list",
  ROADMAP_TASK_REMOVED_FROM_LIST: "roadmap_task_removed_from_list",
  ROADMAP_TASK_OPENED: "roadmap_task_opened",
  // Assign-to-Lev lifecycle: founder delegates a task, then accepts/rejects the
  // proposal Lev drafts, or completes the task by hand. `nodeId` is a template key.
  ROADMAP_TASK_ASSIGNED_TO_LEV: "roadmap_task_assigned_to_lev",
  ROADMAP_TASK_PROPOSAL_ACCEPTED: "roadmap_task_proposal_accepted",
  ROADMAP_TASK_PROPOSAL_REJECTED: "roadmap_task_proposal_rejected",
  ROADMAP_TASK_MANUALLY_COMPLETED: "roadmap_task_manually_completed",
  // v2 ladder model events — see src/constants/ladders.ts
  TASK_ITERATION_PROPOSED: "task_iteration_proposed",
  TASK_ITERATION_SKIPPED: "task_iteration_skipped",
  TASK_RUNG_ADVANCED: "task_rung_advanced",
  TASK_DELETION_PIVOT: "task_deletion_pivot",
  // Weekly report email cron — fired from src/inngest/sendWeeklyReportEmails.ts
  TASK_WEEKLY_REPORT_EMAIL_SENT: "task_weekly_report_email_sent",
  TASK_WEEKLY_REPORT_EMAIL_SKIPPED: "task_weekly_report_email_skipped",
  TASK_WEEKLY_REPORT_EMAIL_FAILED: "task_weekly_report_email_failed",
  // Sunday market-update scan finished (itemCount 0 = nothing new to show).
  WEEKLY_REPORT_MARKET_UPDATE_GENERATED:
    "weekly_report_market_update_generated",
  // Fired client-side when an email "Update Lev" deep-link arms the founder-
  // update capture on load (src/components/organisms/reports/WeeklyReportDeepLinkHandler.tsx).
  TASK_WEEKLY_REPORT_EMAIL_UPDATE_LEV_OPENED:
    "task_weekly_report_email_update_lev_opened",
  // Evaluation events
  EVALUATION_INITIATED: "evaluation_initiated",
  EVALUATION_COMPLETED: "evaluation_completed",
  EVALUATION_VIEWED: "evaluation_viewed",
  EVALUATION_IMPROVEMENT_ADDED_AS_TASK: "evaluation_improvement_added_as_task",
  // Team-only First read preview finished (flag `eval-phased-areas`).
  EVALUATION_FIRST_READ_PREVIEWED: "evaluation_first_read_previewed",
  // A First read button handed a message to Lev chat. `surface` says which
  // First read: the team preview (`eval-phased-areas`) or the founder journey
  // (`eval-first-read-journey`).
  EVALUATION_FIRST_READ_ACTION_CLICKED: "evaluation_first_read_action_clicked",
  // The Locked card's "Have Lev do a first read" was clicked (flag
  // `eval-first-read-journey`); `missingSteps` lists the gate blocks it wrote.
  EVALUATION_FIRST_READ_REQUESTED: "evaluation_first_read_requested",
  // Brutal read finished (flag `eval-brutal-read`).
  EVALUATION_BRUTAL_READ_VIEWED: "evaluation_brutal_read_viewed",
  // Server-side: evaluation.firstRead (flag `eval-first-read-journey`)
  // resolved — fresh, unchanged (cache hit), or failed. Not fired when the
  // journey state is not `first_read`: nothing was generated.
  EVALUATION_FIRST_READ_GENERATED: "evaluation_first_read_generated",
  // Labs events
  LABS_FEATURE_VIEWED: "labs_feature_viewed",
  // Deliverable user-initiated events
  DELIVERABLE_MANUAL_GENERATE_CLICKED: "deliverable_manual_generate_clicked",
  DELIVERABLE_REGENERATED: "deliverable_regenerated",
  // Investor / advisor update events (see docs/plans/2026-05-25-investor-updates-design.md §7)
  INVESTOR_UPDATE_PROMPT_DROPPED: "investor_update_prompt_dropped",
  INVESTOR_UPDATE_REMINDER_SENT: "investor_update_reminder_sent",
  INVESTOR_UPDATE_GENERATION_STARTED: "investor_update_generation_started",
  INVESTOR_UPDATE_GENERATION_COMPLETED: "investor_update_generation_completed",
  INVESTOR_UPDATE_GENERATION_ERRORED: "investor_update_generation_errored",
  INVESTOR_UPDATE_EDITED: "investor_update_edited",
  INVESTOR_UPDATE_COPIED: "investor_update_copied",
  INVESTOR_UPDATE_FORWARDABLE_COPIED: "investor_update_forwardable_copied",
  INVESTOR_UPDATE_INDUSTRY_SECTION_REMOVED: "investor_update_industry_section_removed",
  INVESTOR_UPDATE_ARCHIVE_VIEWED: "investor_update_archive_viewed",
  INVESTOR_UPDATE_ENRICH_STARTED: "investor_update_enrich_started",
  INVESTOR_UPDATE_SECTION_APPLIED: "investor_update_section_applied",
  // Investor-update collection sharing (mirror of the weekly collection share).
  // Counts/booleans/lengths/ids only — never the raw ask or comment text.
  SHARE_INVESTOR_UPDATE_SHARED: "share_investor_update_shared",
  SHARE_INVESTOR_UPDATES_COLLECTION_SHARED: "share_investor_updates_collection_shared",
  SHARE_INVESTOR_UPDATES_COLLECTION_LINK_COPIED: "share_investor_updates_collection_link_copied",
  SHARE_INVESTOR_UPDATES_COLLECTION_DISABLED: "share_investor_updates_collection_disabled",
  INVESTOR_UPDATE_ASK_SET: "investor_update_ask_set",
  INVESTOR_UPDATE_COMMENT_ADDED: "investor_update_comment_added",
  // LEV-2358: founder-idea fit story in the Team block. Enum ratings, counts
  // and lengths only, never the narrative, questions or answer text.
  TEAM_FOUNDER_FIT_GENERATED: "team_founder_fit_generated",
  TEAM_FOUNDER_FIT_QUESTION_ANSWERED: "team_founder_fit_question_answered",
  TEAM_FOUNDER_FIT_VIEWED: "team_founder_fit_viewed",
} as const;

/** Emit a LevAI session summary after this much inactivity. */
export const LEVAI_CHAT_SESSION_IDLE_MS = 5 * 60 * 1000;

/** Accelerator Apps events. Named ANALYTICS_EVENTS_LABS from its Labs-era origin; out of scope to rename here. */
export const ANALYTICS_EVENTS_LABS = {
  // Despite the name, a guarded first draft (automatic, or Draft on an
  // unstarted row) counts only once the server confirms it started one: a
  // refused or failed start does not fire, and the event lands after the
  // round trip. The canvas row's held drafts count the same way. Regenerate,
  // Try again and Retry still fire on the click.
  ACCEL_APP_GENERATE_CLICKED: "accel_app_generate_clicked",
  /**
   * A first draft started with no founder click (accelerators-row): the
   * canvas drafting a held application (`startedBy: "canvas_held_draft"`) or
   * the canvas Application view drafting on open (`"application_view"`), or
   * the program hand-off screen drafting for a phone, whose application page
   * never mounts (`"program_handoff"`, flag `phone-handoff`).
   * Fired only once the server confirms the start, and never alongside
   * accel_app_generate_clicked, so click counts stay clicks.
   */
  ACCEL_APP_AUTO_STARTED: "accel_app_auto_started",
  ACCEL_APP_GENERATED: "accel_app_generated",
  ACCEL_APP_GENERATION_FAILED: "accel_app_generation_failed",
  ACCEL_APP_EVALUATED: "accel_app_evaluated",
  ACCEL_APP_BRUTAL_MODE_RUN: "accel_app_brutal_mode_run",
  // The founder clicked "Get the brutal read" (accelerators-row Improve): the
  // whole-application read by the program's toughest reviewer. Carries the
  // formatId only, never the application or Lev's read.
  ACCEL_APP_BRUTAL_VERDICT_REQUESTED: "accel_app_brutal_verdict_requested",
  // The founder clicked "Draft my interview questions" (a scored application
  // with none): one paid model call. Carries the formatId only, never the
  // application or the questions.
  ACCEL_APP_INTERVIEW_QUESTIONS_DRAFT_REQUESTED: "accel_app_interview_questions_draft_requested",
  // The founder clicked Cancel on a running draft. Carries the formatId only.
  ACCEL_APP_CANCEL_CLICKED: "accel_app_cancel_clicked",
  ACCEL_APP_INTERVIEW_QUESTION_ANSWERED: "accel_app_interview_question_answered",
  ACCEL_APP_REGENERATED: "accel_app_regenerated",
  // The founder clicked Recalculate (every format, YC included): the successor
  // to yc_app_recalculate_clicked. Carries the answer count and whether it
  // only rescores an edited draft; never the answers.
  ACCEL_APP_RECALCULATE_CLICKED: "accel_app_recalculate_clicked",
  // A score the founder's draft or Recalculate asked for landed, for every
  // format (YC included, by formatId): the successor to yc_app_score_calculated.
  // Fires once per request, for the first score that differs from the one on
  // screen when it was asked for; never for a draft the panel did not start.
  ACCEL_APP_SCORE_CALCULATED: "accel_app_score_calculated",
  ACCEL_APP_ANSWER_COPIED: "accel_app_answer_copied",
  // A founder's edit to one structured answer (Techstars) reached the server.
  // Carries the question id, never the text.
  ACCEL_APP_ANSWER_SAVED: "accel_app_answer_saved",
  // A founder saved one Submit "Your details" field (LEV-2442). Carries the
  // field id and whether it is filled, never the value.
  ACCEL_APP_DETAIL_SAVED: "accel_app_detail_saved",
  ACCEL_APP_ANSWERS_COPIED_ALL: "accel_app_answers_copied_all",
  // Lev coached one scored answer (accelerators-row Application block). Carries
  // the question id, stage, confidence and counts; never the answer or Lev's text.
  ACCEL_APP_ANSWER_COACHED: "accel_app_answer_coached",
  // The founder put the coach's tighter version into the editor (Evaluate
  // rows only). Not a save.
  ACCEL_APP_COACH_SUGGESTION_USED: "accel_app_coach_suggestion_used",
  // LEV-2647: the founder clicked "Use this version" on Draft's coach box,
  // which saved the tightened answer straight to the application through
  // the same path a manual Save uses. Carries the question id only; never
  // the answer or Lev's text.
  ACCEL_APP_COACHED_ANSWER_USED: "accel_app_coached_answer_used",
  ACCEL_APP_EXPORTED: "accel_app_exported",
  // The coaching events of every format, YC included: the canvas Application
  // block no longer fires yc_app_suggestion_answered, yc_app_one_liner_selected
  // or yc_app_vision_scale_changed, so a YC funnel reads these by formatId
  // "yc". Each carries formatId; never the answer or one-liner text.
  ACCEL_APP_SUGGESTION_ANSWERED: "accel_app_suggestion_answered",
  ACCEL_APP_ONE_LINER_SELECTED: "accel_app_one_liner_selected",
  ACCEL_APP_VISION_SCALE_CHANGED: "accel_app_vision_scale_changed",
  // The founder saved their own vision statement on Expand (LEV-2504). Carries
  // the length only; never the text.
  ACCEL_APP_VISION_STATEMENT_SAVED: "accel_app_vision_statement_saved",
  INVESTOR_RESEARCH_GENERATE_CLICKED: "investor_research_generate_clicked",
  INVESTOR_RESEARCH_GENERATED: "investor_research_generated",
  INVESTOR_RESEARCH_GENERATION_FAILED: "investor_research_generation_failed",
  INVESTOR_RESEARCH_ROUND_SAVED: "investor_research_round_saved",
  INVESTOR_RESEARCH_IIP_SAVED: "investor_research_iip_saved",
  INVESTOR_RESEARCH_OUTBOUND_EMAIL_GENERATED: "investor_research_outbound_email_generated",
  INVESTOR_RESEARCH_OUTBOUND_EMAIL_GENERATION_FAILED:
    "investor_research_outbound_email_generation_failed",
  INVESTOR_RESEARCH_SUGGESTED_ARTIFACTS_SAVED: "investor_research_suggested_artifacts_saved",
  INVESTOR_RESEARCH_COMPETITOR_SECTION_TOGGLED: "investor_research_competitor_section_toggled",
  INVESTOR_RESEARCH_PROFILE_JUMP_CLICKED: "investor_research_profile_jump_clicked",
  INVESTOR_RESEARCH_PARTNER_LINK_CLICKED: "investor_research_partner_link_clicked",
  INVESTOR_RESEARCH_WARM_INTRO_EMAIL_CLICKED: "investor_research_warm_intro_email_clicked",
  INVESTOR_RESEARCH_VENTURE_GRAPH_LINK_CLICKED: "investor_research_venture_graph_link_clicked",
  MA_ADVISOR_GENERATE_CLICKED: "ma_advisor_generate_clicked",
  MA_ADVISOR_GENERATED: "ma_advisor_generated",
  MA_ADVISOR_GENERATION_FAILED: "ma_advisor_generation_failed",
  MA_ADVISOR_MANDATE_SAVED: "ma_advisor_mandate_saved",
  MA_ADVISOR_CANDIDATE_SELECTED: "ma_advisor_candidate_selected",
  MA_ADVISOR_CANDIDATE_ADDED: "ma_advisor_candidate_added",
  MA_ADVISOR_QUESTION_RESOLVED: "ma_advisor_question_resolved",
  // The Fundraising row. Named for the JOB, not the
  // internal feature, so the funnel reads the way a founder moves through it:
  // pick a path -> bring or build a deck -> get scored -> answer the gaps.
  INVESTOR_STUDIO_FUNDING_PATH_CHOSEN: "investor_studio_funding_path_chosen",
  // Cross-block movement inside the row, from the nav bar at the top of a
  // stage. The bottom of a page says what to do NEXT; this is how a founder
  // leaves a block they did not mean to open.
  INVESTOR_STUDIO_BLOCK_NAV_USED: "investor_studio_block_nav_used",
  INVESTOR_STUDIO_DECK_MODE_CHOSEN: "investor_studio_deck_mode_chosen",
  INVESTOR_STUDIO_DECK_UPLOADED: "investor_studio_deck_uploaded",
  INVESTOR_STUDIO_DECK_UPLOAD_FAILED: "investor_studio_deck_upload_failed",
  INVESTOR_STUDIO_DECK_SCORED: "investor_studio_deck_scored",
  INVESTOR_STUDIO_DECK_VERSION_GENERATED:
    "investor_studio_deck_version_generated",
  INVESTOR_STUDIO_DECK_HANDOFF_COPIED:
    "investor_studio_deck_handoff_copied",
  INVESTOR_STUDIO_DECK_OUTLINE_COPIED: "investor_studio_deck_outline_copied",
  INVESTOR_STUDIO_GAPS_SENT_TO_QUESTIONS:
    "investor_studio_gaps_sent_to_questions",
  INVESTOR_STUDIO_FAQ_COPIED: "investor_studio_faq_copied",
  // The promoted-deck card (LEV-1420 FR4): "score" runs extract->score,
  // "replace" opens the uploader.
  INVESTOR_STUDIO_PROMOTED_DECK_ACTION: "investor_studio_promoted_deck_action",
  // The Fundraising row rebuilt as a linear WIZARD (LEV-1555/1557). The primary
  // block is a static intro panel that hands off to the first deliverable, and
  // each deliverable advances to the next. Ids and counts only — never founder
  // text or AI output.
  //
  // Founder clicked the intro panel's "Start -> Fundraising Path" CTA.
  FUNDRAISING_WIZARD_PRIMARY_CTA_CLICKED: "fundraising_wizard_primary_cta_clicked",
  // Founder advanced from one deliverable to the next via the wizard's forward
  // CTA. Properties: from_deliverable_id, to_deliverable_id (closed set).
  FUNDRAISING_WIZARD_DELIVERABLE_ADVANCED: "fundraising_wizard_deliverable_advanced",
  // Founder revealed the chat on a Fundraising block (it starts hidden there).
  FUNDRAISING_WIZARD_CHAT_REVEALED: "fundraising_wizard_chat_revealed",
  LIFTOFF_STORY_GENERATE_CLICKED: "liftoff_story_generate_clicked",
  LIFTOFF_STORY_GENERATED: "liftoff_story_generated",
  LIFTOFF_STORY_GENERATION_FAILED: "liftoff_story_generation_failed",
  LIFTOFF_EVALUATE_CLICKED: "liftoff_evaluate_clicked",
  LIFTOFF_DECK_EVALUATED: "liftoff_deck_evaluated",
  LIFTOFF_ANSWERS_APPLIED: "liftoff_answers_applied",
  LIFTOFF_VOICE_INPUT_USED: "liftoff_voice_input_used",
  LIFTOFF_EXPORT_COPIED: "liftoff_export_copied",
  LIFTOFF_EXPORT_DOWNLOADED: "liftoff_export_downloaded",
  LIFTOFF_BUILDER_PROMPT_COPIED: "liftoff_builder_prompt_copied",
  LIFTOFF_DECK_THEME_CHANGED: "liftoff_deck_theme_changed",
  LIFTOFF_DECK_STYLE_CHANGED: "liftoff_deck_style_changed",
  LIFTOFF_SLIDE_PHOTO_APPLIED: "liftoff_slide_photo_applied",
  INVESTOR_QUESTIONS_GENERATED: "investor_questions_generated",
  INVESTOR_QUESTIONS_ANSWER_COACHED: "investor_questions_answer_coached",
  INVESTOR_QUESTIONS_ANSWER_SAVED: "investor_questions_answer_saved",
  INVESTOR_QUESTIONS_DOC_EXPORTED: "investor_questions_doc_exported",
  LEVDEV_ASSESSED: "levdev_assessed",
  LEVDEV_BUILDER_SELECTED: "levdev_builder_selected",
  LEVDEV_LAUNCHED: "levdev_launched",
  APP_STORE_ASSESSED: "app_store_assessed",
  CRAFTY_GTM_GENERATE_CLICKED: "crafty_gtm_generate_clicked",
  CRAFTY_GTM_GENERATED: "crafty_gtm_generated",
  CRAFTY_GTM_GENERATION_FAILED: "crafty_gtm_generation_failed",
  PITCH_PERFECT_GENERATED: "pitch_perfect_generated",
  PITCH_PERFECT_GENERATION_FAILED: "pitch_perfect_generation_failed",
  PITCH_PERFECT_SYNTHESIZED: "pitch_perfect_synthesized",
  PITCH_PERFECT_SYNTHESIS_FAILED: "pitch_perfect_synthesis_failed",
  PITCH_PERFECT_AUDIO_PLAYED: "pitch_perfect_audio_played",
  PITCH_PERFECT_VC_EMAIL_GENERATED: "pitch_perfect_vc_email_generated",
  PITCH_PERFECT_CANVAS_PROPOSALS_PROPOSED: "pitch_perfect_canvas_proposals_proposed",
  PITCH_PERFECT_CANVAS_PROPOSALS_APPLIED: "pitch_perfect_canvas_proposals_applied",
  PITCH_PERFECT_DRAFT_SAVED: "pitch_perfect_draft_saved",
  VC_PITCH_UPLOADED: "vc_pitch_uploaded",
  VC_PITCH_RESEARCH_STARTED: "vc_pitch_research_started",
  VC_PITCH_DECISION_SET: "vc_pitch_decision_set",
  VC_PITCH_SHARE_LINK_CREATED: "vc_pitch_share_link_created",
  ACCEL_MATCH_RUN_CLICKED: "accel_match_run_clicked",
  ACCEL_MATCH_GENERATED: "accel_match_generated",
  ACCEL_MATCH_GENERATION_FAILED: "accel_match_generation_failed",
  ACCEL_MATCH_DRAFT_APP_CLICKED: "accel_match_draft_app_clicked",
  // AI Moat. `quadrant` is the property to watch: its distribution across
  // founders is the feature's most interesting signal, and a population that is
  // overwhelmingly wrapper_trap is a product insight in itself.
  AI_MOAT_MAP_GENERATE_CLICKED: "ai_moat_map_generate_clicked",
  // Phase 1 asks two concurrent questions, so there is no single moment a whole
  // map is "generated". mapRunId rejoins the pair into one logical run.
  AI_MOAT_SOURCES_MAPPED: "ai_moat_sources_mapped",
  AI_MOAT_CAPABILITIES_MAPPED: "ai_moat_capabilities_mapped",
  AI_MOAT_MAP_GENERATION_FAILED: "ai_moat_map_generation_failed",
  AI_MOAT_ASSESSED: "ai_moat_assessed",
  AI_MOAT_OPPORTUNITIES_GENERATED: "ai_moat_opportunities_generated",
  AI_MOAT_OPPORTUNITIES_GENERATION_FAILED: "ai_moat_opportunities_generation_failed",
  AI_MOAT_INSUFFICIENT_CONTEXT: "ai_moat_insufficient_context",
} as const;

export const LANDING_PAGE_SLUGS = [
  "home",
  "techstars",
  // The YC program lander (PROGRAMS.yc). Lev has no YC partnership; the slug
  // only names the page.
  "yc",
  "startup-weekend-women",
  "startupday",
  "incisive",
  "investormatch",
  "pack",
  "bluenorth",
  "pinecone",
  "foundernexus",
  "comotion",
  "allen-school",
  "cdl-seattle",
  "chase",
  "wtia",
  "idea-finder",
  "meet",
  // Zeitgeist is an evergreen public tool like idea-finder, not a partner or
  // event page. Its audience is a founder who does not have an idea yet, so it
  // lives on the marketing site rather than behind auth.
  "zeitgeist",
  // Lev Learn is the public concept library at /learn. Unlike the other slugs
  // its audience mostly arrives from search rather than from a campaign, so
  // its page views are the top of the acquisition funnel rather than a
  // campaign response.
  "learn",
  // The public Venture Graph directory at /venturegraph. Like Learn, its
  // audience arrives from search rather than a campaign, so its page views are
  // acquisition rather than campaign response. Registering the slug here also
  // trusts `?src=venturegraph` as a referral id and mints /qr/venturegraph.
  "venturegraph",
] as const;

export type LandingPageSlug = (typeof LANDING_PAGE_SLUGS)[number];

/**
 * Shared landing event actions allowed across slug-derived marketing pages.
 * The full event name is always derived as `landing_${slug}_${action}`.
 */
export const LANDING_PAGE_EVENTS = [
  "viewed",
  "message_sent",
  "file_added",
  "file_parsed",
  "conversion_submitted",
  "idea_finder_clicked",
  "start_own_idea_clicked",
  "form_submitted",
  "results_viewed",
  "card_expanded",
  "card_copied",
  "card_downloaded",
  "build_clicked",
  "reroll_clicked",
  "signup_started",
  "primary_cta_clicked",
  "secondary_cta_clicked",
  "partner_link_clicked",
] as const;

export type LandingPageEventAction = (typeof LANDING_PAGE_EVENTS)[number];
export type LandingPageCustomEventAction = Exclude<LandingPageEventAction, "viewed">;

type ReplaceHyphenWithUnderscore<TValue extends string> =
  TValue extends `${infer THead}-${infer TTail}`
    ? `${THead}_${ReplaceHyphenWithUnderscore<TTail>}`
    : TValue;

type LandingPageEventSlug = ReplaceHyphenWithUnderscore<LandingPageSlug>;

export type LandingPageCustomEventName<
  TAction extends string = LandingPageCustomEventAction,
> =
  `landing_${LandingPageEventSlug}_${TAction}`;

export type LandingPageViewedEventName =
  `landing_${LandingPageEventSlug}_viewed`;

export function getCustomLandingPageEventName<TAction extends LandingPageCustomEventAction>(
  slug: LandingPageSlug,
  action: TAction,
): LandingPageCustomEventName<TAction> {
  return `landing_${slug.replaceAll("-", "_")}_${action}` as LandingPageCustomEventName<TAction>;
}

export function getLandingPageViewedEventName(
  slug: LandingPageSlug,
): LandingPageViewedEventName {
  return `landing_${slug.replaceAll("-", "_")}_viewed` as LandingPageViewedEventName;
}

export type AnalyticsEventName =
  | (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]
  | LandingPageViewedEventName
  | LandingPageCustomEventName
  | (typeof ANALYTICS_EVENTS_LABS)[keyof typeof ANALYTICS_EVENTS_LABS]
  | TasksEvalAnalyticsEventName;

export function getUserSignedUpInsertId(userId: string): string {
  return `${ANALYTICS_EVENTS.USER_SIGNED_UP}:${userId}`;
}

// === LEV-906: Quest/Action analytics ===
// Strict literal-union event names and property unions for the six
// Quest/Action lifecycle events shared by:
//   - `playbookGeneration.ts` (generation, diff-skipped, mechanical rung_advanced)
//   - `actions.updateStatus` mutation (action_completed, quest_completed)
//   - `freeflowGeneration.createLaddersActionRows` (LLM-inferred rung_advanced)
// Per CLAUDE.md, enum properties MUST stay strict literal unions — do not
// widen with `| string`.

export const QUEST_ACTION_ANALYTICS_EVENTS = {
  QUEST_GENERATED: "quest_generated",
  ACTION_GENERATED: "action_generated",
  ACTION_COMPLETED: "action_completed",
  QUEST_COMPLETED: "quest_completed",
  PLAYBOOK_DIFF_SKIPPED: "playbook_diff_skipped",
  RUNG_ADVANCED: "rung_advanced",
  // LEV-1063: Category A synthesis task auto-completed when the founder
  // accepts Lev's proposed primary-block update.
  TASK_COMPLETED_BY_LEV: "task_completed_by_lev",
  // Task-lifecycle telemetry for the Tasks Usage dashboard. The v3 rewrite
  // left defer/delete emitting only structural events; these give PostHog the
  // per-task defer/delete signal, tagged with generation `source` so the
  // dashboard can split user- vs Lev-generated work. Counts/ids/enums only.
  ACTION_DEFERRED: "action_deferred",
  ACTION_DELETED: "action_deleted",
} as const;

export type QuestActionAnalyticsEventName =
  (typeof QUEST_ACTION_ANALYTICS_EVENTS)[keyof typeof QUEST_ACTION_ANALYTICS_EVENTS];

// === LEV-1066: Per-category completion-undo analytics ===
// Strict literal-union event name for the founder-initiated "Undo" on a
// just-completed task. Emitted server-side from the `actions.undoCompletion`
// mutation inside the revert transaction so it only fires when the rollback
// commits. Payload carries booleans/ids only (no raw text), per CLAUDE.md:
//   - hadBlockChange: whether the undo rolled back a promoted BlockVersion
//     (Category A only — Category B reverts the Action with no block change)
//   - cancelledApollo: whether an in-flight Apollo Inngest job was cancelled
//     (Category B Apollo only; false when rows were already acquired or the
//     runtime cancel API is unavailable)
//
// LEV-1067 extends this group with the remaining completion-lifecycle events:
//   - task_classified — fired once per Action when the skeleton generator
//     persists its `completionMode` classification (server-side, in
//     `applyDesignSkeleton`). Payload carries the classification + ladder
//     coordinates only (no titles/text).
//   - ask_lev_clicked — fired CLIENT-side when the founder clicks the "Ask
//     Lev to complete" CTA, before/independent of the mutation result.
//   - lev_completion_proposed — fired CLIENT-side from the completion flow
//     (`handleAskLevComplete` in useCompletionFlow) when Lev returns a block
//     proposal for a Category A task. { projectId, actionId } ids only.
//   - lev_completion_accepted — fired CLIENT-side from the completion flow
//     (`handleAcceptCompletionProposal`) when the founder accepts a Lev-driven
//     (`completedBy === "lev"`) proposal. { projectId, actionId } ids only.
//   - lev_completion_rejected — fired CLIENT-side from the completion flow
//     (`handleRejectCompletionProposal`) when the founder rejects a Lev-driven
//     proposal. { projectId, actionId } ids only.
export const LEV_COMPLETION_ANALYTICS_EVENTS = {
  COMPLETION_UNDONE: "lev_completion_undone",
  TASK_CLASSIFIED: "task_classified",
  ASK_LEV_CLICKED: "ask_lev_clicked",
  COMPLETION_PROPOSED: "lev_completion_proposed",
  COMPLETION_ACCEPTED: "lev_completion_accepted",
  COMPLETION_REJECTED: "lev_completion_rejected",
} as const;

export type LevCompletionAnalyticsEventName =
  (typeof LEV_COMPLETION_ANALYTICS_EVENTS)[keyof typeof LEV_COMPLETION_ANALYTICS_EVENTS];

/**
 * The full per-Action completion classification (LEV-1058). Strict literal
 * union shared by `task_classified` (all three modes) and re-narrowed to the
 * two Lev-driven modes for `ask_lev_clicked`. Mirrors `CompletionMode` in
 * `@/lib/tasks/taskListEngine` — re-declared here so `events.ts` stays a leaf
 * module with no runtime dependency on the tasks engine.
 */
export type CompletionClassificationMode =
  | "lev-synthesis"
  | "lev-deliverable"
  | "human-only";

/** The two completion modes that render the "Ask Lev to complete" CTA. */
/**
 * How an Orbit ICP scan ended (`orbit_prospect_scanned.outcome`). A closed set,
 * never error text: "icp_unreadable" is the committed ICP failing to derive.
 */
export type OrbitProspectScanOutcome = "ok" | "icp_unreadable" | "error";

export type AskLevCompletionMode = "lev-synthesis" | "lev-deliverable";

/**
 * Which completion category the undone task belonged to. Strict literal union
 * — drives funnel slicing for the Undo feature. `lev-synthesis` = Category A
 * (block proposal), `lev-deliverable` = Category B (deliverable-gated),
 * `human-only` = manual completion with no Lev side effect.
 */
export type LevCompletionUndoneMode =
  | "lev-synthesis"
  | "lev-deliverable"
  | "human-only";

/** Reason a playbook rung produced no rows. Strict literal union. */
export type PlaybookDiffSkippedReason =
  | "all_satisfied"
  | "no_playbook"
  | "terminal";

/** Who completed the Action. Strict literal union. */
export type ActionCompletedBy = "user" | "lev";

/** Mechanism that advanced the ladder rung. Strict literal union. */
export type RungAdvancedTrigger = "playbook_closure" | "llm_inferred";

// === LEV-1065: Category B Apollo dispatch telemetry ===
/**
 * Fired server-side when "Ask Lev to complete" on a Category B Discovery task
 * dispatches the Apollo prospect generator because the project has fewer than
 * `APOLLO_MIN_ROWS` Discovery PipelineEntry rows. Counts/ids/booleans only — no
 * raw task text (per CLAUDE.md analytics rules).
 */
export const LEV_APOLLO_ANALYTICS_EVENTS = {
  LEV_APOLLO_DISPATCHED: "lev_apollo_dispatched",
} as const;

export type LevApolloAnalyticsEventName =
  (typeof LEV_APOLLO_ANALYTICS_EVENTS)[keyof typeof LEV_APOLLO_ANALYTICS_EVENTS];

// === LEV-910: Action-reuse telemetry ===
/**
 * Action-reuse decision telemetry. Emitted server-side for every fuzzy-match
 * attempt the Action-reuse matcher makes (LEV-908) so we can compute
 * precision/recall against operator-labeled samples post-launch and tune
 * `ACTION_REUSE_THRESHOLD` from data instead of guesswork.
 *
 * The payload carries only the similarity `score`, the strict-literal
 * `decision` discriminant, ladder coordinates, and a stable SHA-256 `pair_hash`
 * that lets dashboards group by pair without revealing raw Action titles or
 * goals (per CLAUDE.md analytics rules).
 */
export const ANALYTICS_EVENTS_ACTION_REUSE = {
  ACTION_REUSE_DECISION: "action_reuse_decision",
} as const;

/** Strict literal union for `action_reuse_decision`. Never widened to `| string`. */
export type ActionReuseDecisionOutcome = "matched" | "not_matched";

export interface ActionReuseDecisionProperties {
  ladderId: string;
  rungLevel: number;
  /** Raw similarity score in [0, 1]. */
  score: number;
  decision: ActionReuseDecisionOutcome;
  /** SHA-256 hash (16 hex chars) of the normalized, alphabetically-sorted title pair. */
  pair_hash: string;
}

// === LEV-969: Rung-eval integration telemetry ===
/**
 * Rung-eval integration telemetry. Three events surface the cost + outcome of
 * gating rung closure on `ProjectEvaluation` data (LEV-961 → LEV-966):
 *
 *   - `tasks_rung_close_grade_gated` — bridge held a rung the LLM thought
 *     ready because an anchor's grade is below target. Headline observability
 *     signal. Fires once per unmet anchor `gap` in
 *     `freeflowGeneration.createLaddersActionRows` on a
 *     `reason.kind === "grade-anchor-unmet"` verdict.
 *
 *   - `tasks_eval_auto_triggered` — frequency monitoring for the no-debounce
 *     auto-eval enqueue. Fires inside `createLaddersActionRows` alongside the
 *     `app/project.evaluation.requested` send (`trigger: "auto-rung-closure"`).
 *
 *   - `tasks_rubric_gap_iteration_emitted` — iteration-path firing. Fires
 *     once per Action persisted in iteration mode by the ladders task
 *     generator (`runLaddersTaskGeneration`).
 *
 *   - `tasks_eval_path_completed` — an eval checkpoint path (`origin: "eval"`
 *     Quest, task-eval-sync) closed. Deliberately NOT `quest_completed`: that
 *     event is the task engine's ladder-Quest series (Tasks Usage dashboard),
 *     and an eval closure in it would read as a ladderless engine Quest.
 *
 *   - `tasks_eval_step_unlocked` — an eval path step's last blocker finished
 *     and the unlock refresh claimed it (`ai-eval-checkpoint-unlock`). Once per
 *     step and unlock: `$insert_id` keys on the step and its unlock key.
 *
 * Per CLAUDE.md analytics rule, every property is a strict literal union or a
 * narrow string — never widened to `| string`.
 */
export const ANALYTICS_EVENTS_TASKS_EVAL = {
  RUNG_CLOSE_GRADE_GATED: "tasks_rung_close_grade_gated",
  EVAL_AUTO_TRIGGERED: "tasks_eval_auto_triggered",
  RUBRIC_GAP_ITERATION_EMITTED: "tasks_rubric_gap_iteration_emitted",
  EVAL_PATH_COMPLETED: "tasks_eval_path_completed",
  EVAL_STEP_UNLOCKED: "tasks_eval_step_unlocked",
} as const;

export type TasksEvalAnalyticsEventName =
  (typeof ANALYTICS_EVENTS_TASKS_EVAL)[keyof typeof ANALYTICS_EVENTS_TASKS_EVAL];

/**
 * Strict literal union for the LadderId portion of these events' payloads.
 * Mirrors `LADDER_IDS` in `@/constants/ladders` — kept in sync at compile time
 * by the `_LadderIdsMatch` check below. We re-declare the union here (rather
 * than re-exporting the runtime `LADDER_IDS` shape) so `events.ts` stays a
 * leaf module with no runtime dependency on the ladders constants. The
 * `import type` is type-only and produces no runtime code after compilation.
 */
export type TasksEvalLadderId =
  | "customer-discovery"
  | "competitive-landscaping"
  | "market-sizing-pricing"
  | "brand-development"
  | "go-to-market"
  | "product-build"
  | "team";

// Compile-time guard: fails to typecheck if `LADDER_IDS` and
// `TasksEvalLadderId` drift out of sync (a ladder added in the constants
// but missing from the union here, or vice-versa).
type _LadderIdRuntime =
  (typeof import("@/constants/ladders").LADDER_IDS)[keyof typeof import("@/constants/ladders").LADDER_IDS];
type _LadderIdsMatch = [_LadderIdRuntime] extends [TasksEvalLadderId]
  ? [TasksEvalLadderId] extends [_LadderIdRuntime]
    ? true
    : false
  : false;
const _LADDER_IDS_MATCH_TASKS_EVAL_LADDER_ID: _LadderIdsMatch = true;
void _LADDER_IDS_MATCH_TASKS_EVAL_LADDER_ID;

/**
 * Eval grade alphabet used in rung-eval events. Matches the wider `Grade`
 * union in `src/lib/tasks/evalBridge.ts` (the bridge re-grades against the
 * full eval alphabet including `F`).
 */
export type TasksEvalGrade = "A" | "B" | "C" | "D" | "F";

export interface TasksRungCloseGradeGatedProperties {
  ladderId: TasksEvalLadderId;
  rungLevel: number;
  /** Canonical rubric category name (post-alias). */
  category: string;
  /** Null when the anchor category was not present in the stored evaluation. */
  currentGrade: TasksEvalGrade | null;
  requiredGrade: TasksEvalGrade;
}

export interface TasksEvalAutoTriggeredProperties {
  projectId: string;
  ladderId: TasksEvalLadderId;
  rungLevel: number;
}

export interface TasksEvalPathCompletedProperties {
  questId: string;
  /** The evaluation area (rubric criterion name or "Other"); "unknown" when the Quest's details lack one. */
  category: string;
  /** Total checkpoint children on the path, skipped ones included. */
  checkpointCount: number;
}

export interface TasksEvalStepUnlockedProperties {
  /** The step that unlocked. */
  actionId: string;
  /** The step whose completion or skip unlocked it. */
  unlockedByActionId: string;
  /** The evaluation area (the step's `details.categoryName`); "unknown" when it lacks one. */
  category: string;
  owner: "founder" | "lev";
  /** The founder had already added the step to their list while it was gated. */
  revealed: boolean;
  /**
   * `written`: the refreshed text was saved. `failed`: the model call failed or
   * returned nothing usable. `skipped`: the step changed before the write (a
   * founder edit, a start, or a newer unlock), so its text was left alone.
   */
  refresh: "written" | "failed" | "skipped";
  /** The refresh added specifics to the title; false when the title was kept. */
  titleChanged: boolean;
}

export interface TasksRubricGapIterationEmittedProperties {
  ladderId: TasksEvalLadderId;
  rungLevel: number;
  /** Canonical rubric category name (post-alias). */
  category: string;
  /**
   * Synthesized gap descriptor `${current ?? "ungraded"}->${required}`,
   * carried through from `RungClosureReason.gaps` (LEV-961). Stable enough
   * for dashboards to bucket by gap distance.
   */
  gap: string;
}
